import { NavLink } from "react-router-dom";
import { 
  Home, 
  Package, 
  Truck, 
  AlertTriangle, 
  TrendingUp, 
  BarChart3, 
  Settings, 
  MapPin, 
  Shield,
  Receipt,
  Camera,
  FileText,
  CalendarDays
} from "lucide-react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useDriverCheck } from "@/hooks/useDriverCheck";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
} from "@/components/ui/sidebar";

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { isDriver, isAdmin } = useDriverCheck();
  const [userRole, setUserRole] = useState<string>('user');

  useEffect(() => {
    const fetchUserRole = async () => {
      if (user) {
        try {
          const { data } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', user.id)
            .single();
          
          if (data) {
            setUserRole(data.role || 'user');
          }
        } catch (error) {
          console.error('Error fetching user role:', error);
        }
      }
    };

    fetchUserRole();
  }, [user]);

  return (
    <Sidebar
      collapsible="icon"
      className="border-r border-border/50 bg-gradient-to-b from-card/50 to-card backdrop-blur-sm"
      {...props}
    >
      <SidebarHeader className="p-4 border-b border-border/50">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-8 h-8 bg-gradient-primary rounded-lg flex items-center justify-center shadow-soft">
              <Package className="h-5 w-5 text-primary-foreground" />
            </div>
            <div className="absolute -inset-1 bg-gradient-primary rounded-lg blur opacity-30 animate-pulse"></div>
          </div>
          <div className="flex flex-col">
            <h2 className="text-sm font-bold bg-gradient-primary bg-clip-text text-transparent">
              GLASSY FINISH LTD
            </h2>
            <p className="text-xs text-muted-foreground">Co. 15853766</p>
          </div>
        </div>
      </SidebarHeader>
      
      <SidebarContent className="p-2">
        <SidebarGroup>
          <SidebarGroupLabel className="px-3 py-2 text-xs font-semibold text-muted-foreground/80 uppercase tracking-wider">
            Navigation
          </SidebarGroupLabel>
          <SidebarGroupContent className="space-y-1">
            <SidebarMenu>
              {(() => {
                // Define all nav items
                const allItems = [
                  {
                    title: "Dashboard",
                    url: "/",
                    icon: Home,
                    isActive: pathname === "/",
                    gradient: "from-primary/20 to-primary/5",
                    iconColor: "text-primary",
                    driverOnly: true, // Also visible to drivers
                    showToAll: true   // Show to everyone
                  },
                  {
                    title: "Deliveries",
                    url: "/deliveries",
                    icon: Package,
                    isActive: pathname === "/deliveries",
                    gradient: "from-secondary/20 to-secondary/5",
                    iconColor: "text-secondary",
                    driverOnly: false
                  },
                  {
                    title: "Drivers",
                    url: "/drivers", 
                    icon: Truck,
                    isActive: pathname === "/drivers",
                    gradient: "from-accent/20 to-accent/5",
                    iconColor: "text-accent",
                    driverOnly: false
                  },
                  {
                    title: "Incidents",
                    url: "/incidents",
                    icon: AlertTriangle,
                    isActive: pathname === "/incidents",
                    gradient: "from-warning/20 to-warning/5",
                    iconColor: "text-warning",
                    driverOnly: false
                  },
                  {
                    title: "Performance",
                    url: "/performance",
                    icon: TrendingUp,
                    isActive: pathname === "/performance",
                    gradient: "from-success/20 to-success/5",
                    iconColor: "text-success",
                    driverOnly: false
                  },
                  {
                    title: "Analytics",
                    url: "/analytics",
                    icon: BarChart3,
                    isActive: pathname === "/analytics",
                    gradient: "from-primary/20 to-secondary/5",
                    iconColor: "text-primary",
                    driverOnly: false
                  },
                  {
                    title: "Expenses",
                    url: "/expenses",
                    icon: Receipt,
                    isActive: pathname === "/expenses",
                    gradient: "from-secondary/20 to-accent/5",
                    iconColor: "text-secondary",
                    driverOnly: false
                  },
                  {
                    title: "Accounting",
                    url: "/accounting",
                    icon: BarChart3,
                    isActive: pathname === "/accounting",
                    gradient: "from-accent/20 to-success/5",
                    iconColor: "text-accent",
                    driverOnly: false
                  },
                  {
                    title: "Upload Proof",
                    url: "/proof-upload",
                    icon: Camera,
                    isActive: pathname === "/proof-upload",
                    gradient: "from-success/20 to-success/5",
                    iconColor: "text-success",
                    driverOnly: true // This is visible to drivers
                  },
                  {
                    title: "My Shifts",
                    url: "/my-shifts",
                    icon: CalendarDays,
                    isActive: pathname === "/my-shifts",
                    gradient: "from-primary/20 to-primary/5",
                    iconColor: "text-primary",
                    driverOnly: true // Drivers can book/view their shifts
                  },
                  {
                    title: "Upload Documents",
                    url: "/upload-documents",
                    icon: FileText,
                    isActive: pathname === "/upload-documents",
                    gradient: "from-blue-500/20 to-blue-500/5",
                    iconColor: "text-blue-600",
                    driverOnly: false
                  },
                  ...(userRole === 'admin' ? [{
                    title: "Admin Panel",
                    url: "/admin",
                    icon: Shield,
                    isActive: pathname?.startsWith("/admin"),
                    gradient: "from-destructive/20 to-destructive/5",
                    iconColor: "text-destructive",
                    driverOnly: false
                  }] : []),
                  {
                    title: "Settings",
                    url: "/settings",
                    icon: Settings,
                    isActive: pathname === "/settings",
                    gradient: "from-muted-foreground/20 to-muted-foreground/5",
                    iconColor: "text-muted-foreground",
                    driverOnly: false
                  },
                ];
                
                // Filter items: pure drivers (not admins) only see driver items
                const visibleItems = (isDriver && !isAdmin)
                  ? allItems.filter(item => item.driverOnly)
                  : allItems;
                  
                return visibleItems;
              })().map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton 
                    asChild 
                    isActive={item.isActive}
                    className={`
                      relative overflow-hidden transition-all duration-200 group
                      ${item.isActive 
                        ? `bg-gradient-to-r ${item.gradient} text-foreground border border-border/30 shadow-soft` 
                        : 'hover:bg-accent/50 hover:text-accent-foreground'
                      }
                    `}
                  >
                    <NavLink 
                      to={item.url}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg relative z-10"
                    >
                      <div className={`transition-all duration-200 ${
                        item.isActive ? item.iconColor : 'text-muted-foreground group-hover:text-foreground'
                      }`}>
                        <item.icon className="h-5 w-5" />
                      </div>
                      <span className={`font-medium transition-all duration-200 ${
                        item.isActive ? 'text-foreground' : 'text-muted-foreground group-hover:text-foreground'
                      }`}>
                        {item.title}
                      </span>
                      {item.isActive && (
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-pulse"></div>
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}